<template>
	<ul class="site-menu">
		<li
			v-for="(item, index) in menuItems"
			:key="index"
			class="site-menu__item"
			:title="item.title"
			v-bind:class="[index === openIndex ? 'is-open' : '']"
		>
			<div v-if="hasSubMenu(item)">
				<button
					exact-active-class="is-active"
					active-class="is-active"
					class="site-menu__link"
					@click="handleSubMenuDisplay(index)"
				>
					{{ item.text }} <i class="icon icon-arrow-down"></i>
				</button>
				<SiteMenuSubmenu :menus="item.subMenu" @show="closeAllMenus" />
			</div>
			<div
				@click="closeAllMenus"
				class="site-menu__item"
				:title="item.title"
				v-else
			>
				<router-link
					:to="{
						path: item.path
					}"
					exact-active-class="is-active"
					active-class="is-active"
					class="site-menu__link"
					>{{ item.text }}
				</router-link>
			</div>
		</li>
	</ul>
</template>

<script>
import SiteMenuSubmenu from '@/site-components/site-menu-submenu/site-menu-submenu';
import DocumentClickedMixin from '@/mixins/document-click';

// vuex
import { mapState } from 'vuex';

// libraries
import _ from 'lodash';
import { deepEqualNoFn } from '@/utils/object';
import AuthorizationManager from '@/authorization/authorization-manager';

export default {
	name: 'SiteMenu',
	mixins: [DocumentClickedMixin('closeHandler')],
	components: {
		SiteMenuSubmenu
	},
	data: function() {
		return {
			openIndex: -1,
			menuItems: [],
			allMenuItems: []
		};
	},
	created() {
		this.closeAllMenus();
	},
	computed: {
		...mapState('menu', ['menu'])
	},
	methods: {
		hasSubMenu(menuItem) {
			return menuItem.subMenu && menuItem.subMenu.length > 0;
		},
		closeAllMenus() {
			this.openIndex = -1;
		},
		handleSubMenuDisplay(idx) {
			this.closeAllMenus();
			this.openIndex = idx;
		},
		closeHandler() {
			this.$emit('close');
			this.closeAllMenus();
		},
		updateAllMenuItems() {
			const validateMenuItems = menuItems => {
				const validPermissions = menuItems.filter(permission => {
					if (
						AuthorizationManager.validate(permission.authorization)
					) {
						if (
							permission.subMenu &&
							permission.subMenu.length > 0
						) {
							permission.subMenu = validateMenuItems(
								permission.subMenu
							);
						}
						return true;
					}
					return false;
				});
				return validPermissions;
			};
			this.menuItems = validateMenuItems(this.allMenuItems);
		}
	},
	watch: {
		menu: {
			immediate: true,
			deep: true,
			handler(newValue, oldValue) {
				if (!deepEqualNoFn(newValue, oldValue) && newValue.length > 0) {
					this.allMenuItems = [];
					this.allMenuItems = _.cloneDeep(newValue);

					this.updateAllMenuItems();
				}
			}
		}
	}
};
</script>

<style lang="scss" scoped>
/**
    * Site Menu
    * Can use SCSS variables = TRUE
 */

.site-menu {
	z-index: 10;
	min-width: 320px;

	@include mq($from: lg) {
		display: inline-flex;
		align-items: center;
	}
}

.site-menu__item {
	position: relative;

	@include mq($from: lg) {
		&:not(:first-child) {
			margin-left: $gutter-md;
		}
	}

	* {
		transition: transform 0.2s ease-in-out, max-height 0.4s ease-in-out,
			opacity 0.4s ease-in;
	}

	&.is-open {
		.site-submenu {
			left: 0;
			top: 100%;
			opacity: 1; //for transition animation
			height: auto; //for transition animation
			max-height: 100vh; //for transition animation
		}
	}
}

.site-menu__link {
	display: flex;
	position: relative;
	align-items: center;
	justify-content: flex-start;
	text-decoration: none;
	padding: $padding-md;
	text-transform: uppercase;
	font-weight: $medium-font-weight;
	@include text-contrast($site-nav-bkgd, $white, $black);

	@include mq($from: lg) {
		font-size: $menu-font-size;
		padding: $padding-sm;
	}

	&:hover,
	&:focus {
		background-color: $neutral-1;
	}
	&:hover,
	&:focus,
	&.is-active {
		@include mq($until: lg) {
			background-color: $neutral-1;

			&:before {
				content: '';
				display: block;
				position: absolute;
				background-image: linear-gradient(
					180deg,
					$base 0%,
					$accent 50%
				);
				width: 4px;
				height: 100%;
				left: -4px; //matches width value
			}
		}

		@include mq($from: lg) {
			&:after {
				content: '';
				display: block;
				position: absolute;
				background-image: linear-gradient(90deg, $base 0%, $accent 50%);
				width: 100%;
				height: 4px;
				bottom: -4px; //matches height value
				left: 0;
			}
		}
	}
}

button.site-menu__link {
	/* clear standard button styles when used with button tag for items with subnav */
	-webkit-user-select: none;
	-moz-user-select: none;
	-ms-user-select: none;
	user-select: none;
	cursor: pointer;
	outline: 0;
	background: transparent;
	border: none;
	width: 100%;

	.icon {
		margin-left: $gutter-md;
		.is-open > & {
			transform: rotate(180deg);
		}
	}
}
</style>
